import { supabase } from '@/lib/supabase';

export type AgreementParty = {
  id: string;
  agreement_id: string;
  /** null όσο ο συμβαλλόμενος δεν έχει εγγραφεί ακόμα (βλ. party_email). */
  user_id: string | null;
  /** Μόνο όταν έχει προσκληθεί με email και δεν είναι ακόμα εγγεγραμμένος. */
  party_email: string | null;
  role: 'creator' | 'party';
  share_percent: number;
  has_signed: boolean;
  signed_at: string | null;
  /** null = δεν έχει ψηφίσει ακόμα σε τρέχον αίτημα ακύρωσης, true = συμφωνεί. */
  cancel_vote: boolean | null;
  /** Μη μηδενικό αν το μέλος έχει αποχωρήσει μονομερώς από τη συμφωνία. */
  withdrawn_at: string | null;
};

export async function fetchAgreementParties(agreementId: string): Promise<AgreementParty[]> {
  const { data, error } = await supabase
    .from('agreement_parties')
    .select('id, agreement_id, user_id, party_email, role, share_percent, has_signed, signed_at, cancel_vote, withdrawn_at')
    .eq('agreement_id', agreementId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AgreementParty[];
}

/** Ζητά ακύρωση συμφωνίας πολλών ατόμων — χρειάζεται τη συναίνεση όλων. */
export async function requestMultiPartyCancellation(agreementId: string, tags: string[]): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('request_multi_party_cancellation', {
    p_agreement_id: agreementId, p_tags: tags,
  });
  if (error) throw new Error(error.message);
}

/** Απαντά σε αίτημα ακύρωσης συμφωνίας πολλών ατόμων. */
export async function respondMultiPartyCancellation(
  agreementId: string, agree: boolean, tags?: string[],
): Promise<{ cancelled: boolean; rejected: boolean; agreed?: number; total?: number }> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('respond_multi_party_cancellation', {
    p_agreement_id: agreementId, p_agree: agree, p_tags: tags ?? null,
  });
  if (error) throw new Error(error.message);
  return data;
}

/** Μονομερής αποχώρηση από συμφωνία πολλών ατόμων — η συμφωνία συνεχίζει για τους υπόλοιπους. */
export async function withdrawFromMultiPartyAgreement(agreementId: string, tags: string[]): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('withdraw_from_multi_party_agreement', {
    p_agreement_id: agreementId, p_tags: tags,
  });
  if (error) throw new Error(error.message);
}

export async function createMultiPartyAgreement(input: {
  title: string;
  description: string | null;
  amount: number | null;
  currency: string;
  creatorShare: number;
  partyIds: string[];
  shares: number[];
  includeProfessionalTerms?: boolean;
  /** Emails μη εγγεγραμμένων ατόμων που προσκαλούνται στη συμφωνία (ίδια λογική με το 2-ατόμων). */
  partyEmails?: string[];
  emailShares?: number[];
  type?: 'one_off' | 'ongoing' | 'fixed_term';
  expiresAt?: string | null;
  expiresAtLabel?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('create_multi_party_agreement', {
    p_title: input.title,
    p_description: input.description,
    p_amount: input.amount,
    p_currency: input.currency,
    p_creator_share: input.creatorShare,
    p_party_ids: input.partyIds,
    p_shares: input.shares,
    p_include_professional_terms: input.includeProfessionalTerms ?? true,
    p_party_emails: input.partyEmails ?? null,
    p_email_shares: input.emailShares ?? null,
    p_type: input.type ?? 'one_off',
    p_expires_at: input.expiresAt ?? null,
    p_expires_at_label: input.expiresAtLabel ?? null,
    p_start_date: input.startDate ?? null,
    p_end_date: input.endDate ?? null,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function signMultiPartyAgreement(agreementId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('sign_multi_party_agreement', { p_agreement_id: agreementId });
  if (error) throw new Error(error.message);
}

export async function openMultiPartyDispute(agreementId: string, accusedId: string, tags: string[], statement?: string, evidenceUrls?: string[]): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('open_multi_party_dispute', {
    p_agreement_id: agreementId, p_accused_id: accusedId, p_tags: tags, p_statement: statement?.trim() || null,
    p_evidence_urls: evidenceUrls ?? null,
  });
  if (error) throw new Error(error.message);
  return (data as { dispute_id: string })?.dispute_id;
}

export async function respondMultiPartyDispute(disputeId: string, accept: boolean, tags: string[], statement?: string, evidenceUrls?: string[]): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('respond_multi_party_dispute', {
    p_dispute_id: disputeId, p_accept: accept, p_tags: tags, p_statement: statement?.trim() || null,
    p_evidence_urls: evidenceUrls ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function castDisputeVote(disputeId: string, voteFor: string | null): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.rpc as any)('cast_dispute_vote', { p_dispute_id: disputeId, p_vote_for: voteFor });
  if (error) throw new Error(error.message);
}

export type MultiPartyDispute = {
  id: string;
  agreement_id: string;
  initiated_by: string;
  accused_user_id: string | null;
  status: string;
  respondent_decision: string | null;
  outcome: string | null;
  voting_expires_at: string | null;
};

export async function fetchMultiPartyDispute(agreementId: string): Promise<MultiPartyDispute | null> {
  const { data, error } = await supabase
    .from('disputes')
    .select('id, agreement_id, initiated_by, accused_user_id, status, respondent_decision, outcome, voting_expires_at')
    .eq('agreement_id', agreementId)
    .not('accused_user_id', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data as MultiPartyDispute | null;
}

/** Πλήθος συμβαλλομένων ανά agreement_id, μόνο για συμφωνίες πολλών ατόμων (>2). */
export async function fetchPartyCounts(agreementIds: string[]): Promise<Record<string, number>> {
  if (agreementIds.length === 0) return {};
  const { data, error } = await supabase
    .from('agreement_parties')
    .select('agreement_id')
    .in('agreement_id', agreementIds);
  if (error) throw error;
  const counts: Record<string, number> = {};
  for (const row of (data ?? []) as { agreement_id: string }[]) {
    counts[row.agreement_id] = (counts[row.agreement_id] ?? 0) + 1;
  }
  return counts;
}

export async function fetchDisputeVotes(disputeId: string): Promise<{ voter_id: string; vote_for: string | null }[]> {
  const { data, error } = await supabase
    .from('dispute_votes')
    .select('voter_id, vote_for')
    .eq('dispute_id', disputeId);
  if (error) throw error;
  return data ?? [];
}

/** Λόγοι (tags) και των δύο πλευρών μιας διαφωνίας πολλών ατόμων — ορατά μόνο
 * σε ψηφοφόρους (RLS), όχι στα ίδια τα εμπλεκόμενα μέρη. */
export async function fetchDisputeStatements(disputeId: string): Promise<{ user_id: string; tags: string[]; statement: string | null }[]> {
  const { data, error } = await supabase
    .from('dispute_statements')
    .select('user_id, tags, statement')
    .eq('dispute_id', disputeId);
  if (error) throw error;
  return (data ?? []) as unknown as { user_id: string; tags: string[]; statement: string | null }[];
}
